import React, { useEffect, useState } from 'react';
import { superAdminApi } from '@/services/superAdminApi';
import { MessageCircle, Save, Loader2, SlidersHorizontal, X, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const CATS = [
  ['marketing', 'Marketing'],
  ['utility', 'Utility'],
  ['authentication', 'Authentifizierung'],
  ['service', 'Service'],
];

const money = (n, cur) => `${cur || 'CHF'} ${(Number(n) || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function WhatsAppBillingPage() {
  const [report, setReport] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [form, setForm] = useState({ markup_percent: '', rates: {} });
  const [range, setRange] = useState({ from: '', to: '' });
  const [editTenant, setEditTenant] = useState(null); // tenant row being overridden
  const [ov, setOv] = useState({ markup_percent: '', rates: {} });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (range.from) params.date_from = range.from;
      if (range.to) params.date_to = range.to;
      const [r, c] = await Promise.all([superAdminApi.waBillingReport(params), superAdminApi.waBillingConfig()]);
      setReport(r.data);
      setConfig(c.data);
      setForm({
        markup_percent: String(c.data.markup_percent ?? 0),
        rates: { ...(c.data.category_rates || {}) },
      });
    } catch { toast.error('Daten konnten nicht geladen werden'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveGlobal = async () => {
    setBusy('global');
    try {
      const rates = {};
      CATS.forEach(([k]) => { rates[k] = Number(form.rates[k] || 0); });
      await superAdminApi.waBillingSaveConfig({ markup_percent: Number(form.markup_percent || 0), category_rates: rates });
      toast.success('Globale Sätze gespeichert');
      await load();
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setBusy(''); }
  };

  const openOverride = (t) => {
    const cur = (config?.overrides || {})[t.tenant_id] || {};
    setEditTenant(t);
    setOv({
      markup_percent: cur.markup_percent != null ? String(cur.markup_percent) : '',
      rates: { ...(cur.category_rates || {}) },
    });
  };

  const saveOverride = async () => {
    if (!editTenant) return;
    setBusy('ov');
    try {
      const rates = {};
      CATS.forEach(([k]) => { if (ov.rates[k] !== '' && ov.rates[k] != null) rates[k] = Number(ov.rates[k]); });
      const body = {
        markup_percent: ov.markup_percent === '' ? null : Number(ov.markup_percent),
        category_rates: Object.keys(rates).length ? rates : null,
      };
      await superAdminApi.waBillingSetOverride(editTenant.tenant_id, body);
      toast.success(`Sonderkonditionen für ${editTenant.name} gespeichert`);
      setEditTenant(null);
      await load();
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setBusy(''); }
  };

  const clearOverride = async () => {
    if (!editTenant) return;
    setBusy('ov');
    try {
      await superAdminApi.waBillingSetOverride(editTenant.tenant_id, { clear: true });
      toast.success('Auf Standard zurückgesetzt');
      setEditTenant(null);
      await load();
    } catch { toast.error('Aktion fehlgeschlagen'); }
    finally { setBusy(''); }
  };

  if (loading) return <div className="text-slate-600" data-testid="sa-wabilling-loading">Lädt…</div>;
  if (!report) return <div className="text-rose-600">Daten konnten nicht geladen werden.</div>;

  const cur = report.currency || 'CHF';

  return (
    <div data-testid="sa-wabilling" className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">WhatsApp-Gebühren weiterverrechnen</h1>
        <p className="mt-1 text-sm text-slate-600">Abrechnung pro Meta-Konversation je Betrieb — Basissatz nach Kategorie plus Aufschlag. Nicht abrechenbare Konversationen werden nicht berechnet.</p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-700"><MessageCircle className="h-5 w-5" /></div>
          <div className="text-3xl font-semibold text-slate-900">{report.totals.conversations}</div>
          <div className="text-sm text-slate-600">Abrechenbare Konversationen</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="text-xs uppercase tracking-wide text-slate-500">Nach Kategorie</div>
          <div className="mt-2 grid grid-cols-2 gap-x-4 text-sm text-slate-700">
            {CATS.map(([k, label]) => (<div key={k}>{label}: <span className="font-medium">{report.totals.by_category?.[k] || 0}</span></div>))}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="text-xs uppercase tracking-wide text-slate-500">Weiterverrechenbar gesamt</div>
          <div className="mt-2 text-3xl font-semibold text-emerald-700">{money(report.totals.amount, cur)}</div>
          <div className="text-xs text-slate-500">inkl. Aufschlag</div>
        </div>
      </div>

      {/* Globale Sätze */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-wabilling-global">
        <h2 className="mb-1 text-lg font-medium text-slate-900">Globale Sätze ({cur})</h2>
        <p className="mb-4 text-xs text-slate-500">Basispreis pro Konversation je Kategorie und standardmässiger Aufschlag. Gilt für alle Betriebe ohne Sonderkonditionen.</p>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          {CATS.map(([k, label]) => (
            <div key={k}>
              <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
              <input type="number" step="0.01" min="0" value={form.rates[k] ?? ''} onChange={(e) => setForm({ ...form, rates: { ...form.rates, [k]: e.target.value } })}
                className="w-full rounded-lg border border-slate-200 bg-slate-100 px-2.5 py-1.5 text-slate-800" data-testid={`sa-wabilling-rate-${k}`} />
            </div>
          ))}
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Aufschlag %</label>
            <input type="number" step="1" min="0" value={form.markup_percent} onChange={(e) => setForm({ ...form, markup_percent: e.target.value })}
              className="w-full rounded-lg border border-slate-200 bg-slate-100 px-2.5 py-1.5 text-slate-800" data-testid="sa-wabilling-markup" />
          </div>
        </div>
        <div className="mt-4">
          <button onClick={saveGlobal} disabled={busy === 'global'} className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60" data-testid="sa-wabilling-save-global">
            {busy === 'global' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Sätze speichern
          </button>
        </div>
      </div>

      {/* Zeitraum + Tabelle */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-wabilling-table">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <h2 className="text-lg font-medium text-slate-900">Pro Betrieb</h2>
          <div className="flex items-end gap-2">
            <div>
              <label className="mb-1 block text-[11px] text-slate-500">Von</label>
              <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-sm text-slate-800" data-testid="sa-wabilling-from" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-slate-500">Bis</label>
              <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-sm text-slate-800" data-testid="sa-wabilling-to" />
            </div>
            <button onClick={load} className="rounded-lg bg-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-300" data-testid="sa-wabilling-apply-range">Anwenden</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="pb-3">Betrieb</th>
              {CATS.map(([k, label]) => <th key={k} className="pb-3 text-right">{label}</th>)}
              <th className="pb-3 text-right">Konv.</th>
              <th className="pb-3 text-right">Aufschlag</th>
              <th className="pb-3 text-right">Betrag</th>
              <th className="pb-3 text-right">Konditionen</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-200">
              {report.tenants.map((t) => (
                <tr key={t.tenant_id} data-testid={`sa-wabilling-row-${t.slug}`}>
                  <td className="py-3 text-slate-800">{t.name}<div className="text-xs text-slate-500">{t.slug}</div></td>
                  {CATS.map(([k]) => <td key={k} className="py-3 text-right text-slate-600">{t.breakdown?.[k]?.count || 0}</td>)}
                  <td className="py-3 text-right font-medium text-slate-800">{t.conversations}</td>
                  <td className="py-3 text-right text-slate-600">{t.markup_percent}%</td>
                  <td className="py-3 text-right font-semibold text-emerald-700">{money(t.amount, cur)}</td>
                  <td className="py-3 text-right">
                    <button onClick={() => openOverride(t)} className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs ${t.has_override ? 'bg-amber-500/15 text-amber-700 hover:bg-amber-500/25' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`sa-wabilling-override-${t.slug}`}>
                      <SlidersHorizontal className="h-3.5 w-3.5" /> {t.has_override ? 'Sonderpreis' : 'Standard'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Override-Modal */}
      {editTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid="sa-wabilling-override-modal">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">Sonderkonditionen — {editTenant.name}</h3>
              <button onClick={() => setEditTenant(null)} className="text-slate-400 hover:text-slate-700" data-testid="sa-wabilling-override-close"><X className="h-5 w-5" /></button>
            </div>
            <p className="mb-4 text-xs text-slate-500">Leer lassen = globaler Standard verwenden. Nur gefüllte Felder überschreiben den Standard.</p>
            <div className="grid grid-cols-2 gap-3">
              {CATS.map(([k, label]) => (
                <div key={k}>
                  <label className="mb-1 block text-xs font-medium text-slate-600">{label} ({cur})</label>
                  <input type="number" step="0.01" min="0" placeholder={`Std. ${config?.category_rates?.[k] ?? 0}`} value={ov.rates[k] ?? ''} onChange={(e) => setOv({ ...ov, rates: { ...ov.rates, [k]: e.target.value } })}
                    className="w-full rounded-lg border border-slate-200 bg-slate-100 px-2.5 py-1.5 text-slate-800" data-testid={`sa-wabilling-ov-rate-${k}`} />
                </div>
              ))}
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Aufschlag %</label>
                <input type="number" step="1" min="0" placeholder={`Std. ${config?.markup_percent ?? 0}`} value={ov.markup_percent} onChange={(e) => setOv({ ...ov, markup_percent: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 bg-slate-100 px-2.5 py-1.5 text-slate-800" data-testid="sa-wabilling-ov-markup" />
              </div>
            </div>
            <div className="mt-6 flex items-center justify-between gap-2">
              <button onClick={clearOverride} disabled={busy === 'ov'} className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700" data-testid="sa-wabilling-ov-clear">
                <RotateCcw className="h-4 w-4" /> Standard verwenden
              </button>
              <button onClick={saveOverride} disabled={busy === 'ov'} className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60" data-testid="sa-wabilling-ov-save">
                {busy === 'ov' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
